import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { FontAwesome } from '@expo/vector-icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import Moment from 'moment';
import Colors from '../../../constants/Colors';
import Style from '../../../constants/Style';
import PressableStyled from '../../PressableStyled';
import DateLabel from '../../Occupancy/DateLabel';
import LineGraph from '../../../graphs/LineGraph';
import GetMetric, { AggregationFunction } from '../../../requests/Sensors/GetMetric';
import { DateRange, isLiveMode, updateLastPoll } from '../../../redux/dateRangeSlice';
import { AreaType, Space } from '../SetFloorsModal/FloorItem';
import CountWidget from './components/CountWidget';
import UtilisationWidget from './components/UtilisationWidget';
import BookingsWidget from './components/BookingsWidget';
import mapOccupancyConfig, { defaultMapOccupancyConfig, MapOccupancyWidgetType } from './config';
import GraphDateLabel from './components/GraphDateLabel';
import { Metric } from '../../../constants/Types';
import SensorsOffline from './components/SensorsOffline';
import { ChartDataPoint } from '../../../graphs/axisUtils';
import getSpaces from '../../../requests/Locations/Spaces/GetSpaces';
import QueueWidget from './components/QueueWidget';
import { AppScreen } from '../../../navigation/LinkingConfiguration';
import { getRangeHours, mapResponseToChartDataPoint } from '../../../constants/Utils';
import BarGraph from '../../../graphs/BarGraph';

interface OccupancyWidgetsProps {
  name: string,
  locationType: AreaType | null,
  goBack: () => void,
  buildingId: string,
  floorId: string | null,
  areaId: string | null,
  spaceId: string | null,
  externalId: string | null,
  capacity: number | null;
  occupancyPercentage?: number | null;
}

const OccupancyWidgets = ({
  name, locationType, goBack, buildingId, floorId, areaId, spaceId, externalId, capacity,
  occupancyPercentage,
}: OccupancyWidgetsProps) => {
  const navigation = useNavigation();
  const range: DateRange = useSelector((state: { dateRange: DateRange }) => (state.dateRange));
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [lineGraphData, setLineGraphData] = useState<ChartDataPoint[] | undefined | null>([]);
  const [barGraphData, setBarGraphData] = useState<ChartDataPoint[] | undefined | null>([]);
  const [sublocationQueueData, setSublocationQueueData] = useState<ChartDataPoint[]
    | undefined | null>([]);

  const showGraphLegend: boolean = sublocationQueueData?.length > 0;
  const queueCount: number = sublocationQueueData?.length > 0
    ? sublocationQueueData[sublocationQueueData.length - 1].value : null;

  const empty: boolean = false && !(lineGraphData?.length > 0);

  const lineGraphProps = {
    chartData: lineGraphData
      ?.map((point: ChartDataPoint, index: number) => ({ ...point, index })),
    style: styles.lineGraph,
    horizontalTickCount: 3,
    noTitle: true,
    showLegend: showGraphLegend,
    onClick: (date: Date) => setSelectedDate(date),
    chartHeight: 315,
    loading: lineGraphData === undefined,
  };

  const dispatch = useDispatch();

  const fetchMetrics: () => void = useCallback(() => {
    setLineGraphData(undefined);
    GetMetric({
      metric: Metric.OCCUPANCY,
      range,
      aggregationFunction: AggregationFunction.MAX,
      externalId: externalId ?? name,
    }).then((res) => {
      setLineGraphData(mapResponseToChartDataPoint(res));
      dispatch(updateLastPoll(Date.now()));
    }).catch(() => setLineGraphData(null));
  }, [externalId, name, range.start, range.end, range.selectedRange]);

  useEffect(() => {
    if (!selectedDate) {
      setBarGraphData(undefined);
      return;
    }
    setBarGraphData(undefined);
    const period: 'day' | 'hour' = getRangeHours(range) <= 24 ? 'hour' : 'day';
    const interval: '1h' | '10m' = getRangeHours(range) <= 24 ? '10m' : '1h';
    GetMetric({
      metric: Metric.OCCUPANCY,
      range: {
        start: Moment(selectedDate).startOf(period).toISOString(),
        end: Moment(selectedDate).endOf(period).toISOString(),
      },
      aggregationFunction: AggregationFunction.MAX,
      externalId: externalId ?? name,
      interval,
    }).then((res) => setBarGraphData(mapResponseToChartDataPoint(res)))
      .catch(() => setBarGraphData(null));
  }, [selectedDate]);

  useEffect(fetchMetrics, [fetchMetrics]);

  useEffect(() => {
    if (spaceId || !areaId) {
      setSublocationQueueData([]);
      return;
    }
    getSpaces(areaId).then(async (res) => {
      if (!(res?.locations?.length > 0)) {
        setSublocationQueueData([]);
        return;
      }
      const queues: Space[] = res.locations.filter((location: Space) => location.LocationSubtype
        === AreaType.QUEUE);
      const queueData = await Promise.all(queues.map((queue: Space) => GetMetric({
        metric: Metric.OCCUPANCY,
        range,
        aggregationFunction: AggregationFunction.MAX,
        externalId: queue.ExternalId,
      })));
      const combinedQueueData = (queueData ?? [])
        .reduce((acc, data) => {
          acc.push(...(data ?? []));
          return acc;
        }, []);
      setSublocationQueueData(mapResponseToChartDataPoint(combinedQueueData));
    });
  }, [areaId, spaceId]);

  let config: MapOccupancyWidgetType[] = defaultMapOccupancyConfig;
  if (mapOccupancyConfig.area && mapOccupancyConfig.area[locationType]
    && mapOccupancyConfig.area[locationType][isLiveMode(range) ? 'live' : 'historic']) {
    config = mapOccupancyConfig.area[locationType][isLiveMode(range) ? 'live' : 'historic'];
  }

  const viewDetails: () => void = () => {
    navigation.navigate(AppScreen.OCCUPANCY as any, {
      buildingId, floorId, areaId, spaceId,
    });
  };

  const BarGraphComponent = () => <View>
    <GraphDateLabel
      label={Moment(selectedDate).format('MMMM D')}
      back={() => setSelectedDate(null)}
    />
    <BarGraph
      title={locationType === AreaType.QUEUE ? 'Queue' : 'Occupancy'}
      chartData={barGraphData?.map((point: ChartDataPoint, index: number) => ({ ...point, index }))}
      horizontalTickCount={3}
      showLegend={showGraphLegend}
      chartHeight={210}
      style={styles.barGraph}
      loading={barGraphData === undefined}
    />
  </View>;

  return <View
    style={styles.container}
    testID='occupancy-widget'
  >
    <View style={[Style.row, styles.header]}>
      <PressableStyled
        style={styles.iconContainer}
        onPress={goBack}
      >
        <FontAwesome
          name='angle-left'
          color={Colors.light.hereworksBlue500}
          size={24}
        />
      </PressableStyled>
      <View style={{ flex: 1 }}>
        <Text style={styles.name}>{name ?? '-'}</Text>
        <DateLabel textStyle={styles.date} />
      </View>
    </View>
    {(empty ? <SensorsOffline /> : config.map((type: MapOccupancyWidgetType) => {
      switch (type) {
        case MapOccupancyWidgetType.COUNT: return <CountWidget
          {...{ viewDetails }}
          limit={capacity}
          occupancy={isLiveMode(range) ? lineGraphData?.[lineGraphData.length - 1]?.value
            : Math.round(occupancyPercentage * capacity)}
          percent={occupancyPercentage}
        />;
        case MapOccupancyWidgetType.UTILISATION:
          return <UtilisationWidget
            {...{ live: isLiveMode(range) }}
            limit={capacity}
            occupancy={lineGraphData?.[lineGraphData.length - 1]?.value}
          />;
        case MapOccupancyWidgetType.QUEUE: return <QueueWidget {...{ queueCount }} />;
        case MapOccupancyWidgetType.BOOKINGS: return <BookingsWidget />;
        case MapOccupancyWidgetType.GRAPH:
          return selectedDate ? <BarGraphComponent /> : <LineGraph
            title={locationType === AreaType.QUEUE ? 'Queue' : 'Occupancy'}
            {...lineGraphProps}
          />;
        case MapOccupancyWidgetType.TRAFFIC:
          return <>
            <Text style={styles.title}>{'Traffic history'}</Text>
            {selectedDate ? <BarGraphComponent /> : <LineGraph
              title='Attendance'
              alternateTitle='Attendance'
              {...lineGraphProps}
            />}
          </>;
        default: return null;
      }
    }))}
  </View>;
};

const styles = StyleSheet.create({
  barGraph: {
    marginEnd: 20,
    marginStart: -16,
  },
  container: {
    paddingHorizontal: 24,
    paddingTop: 12,
    paddingBottom: 32,
  },
  header: {
    borderRadius: 8,
    backgroundColor: Colors.light.hereworksWhite200,
    paddingStart: 4,
    paddingEnd: 12,
    paddingVertical: 8,
    marginBottom: 32,
  },
  iconContainer: {
    width: 32,
    height: 32,
    padding: 4,
    alignItems: 'center',
    justifyContent: 'center',
    marginEnd: 4,
  },
  name: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
  },
  date: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  lineGraph: {
    marginTop: -80,
    marginStart: -28,
    marginEnd: -4,
  },
  title: {
    fontSize: 16,
    lineHeight: 20,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack500,
    paddingTop: 24,
    borderTopColor: Colors.light.hereworksWhite500,
    borderTopWidth: 1,
  },
});

export default OccupancyWidgets;
